import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { collection, limit, query, where } from "firebase/firestore";
import { db } from "../lib/firebase";
import { useAuth, type UserRole } from "../lib/auth";
import { useCollection } from "../hooks/useCollection";

/** Eski newuser.php:124 ile aynı etiketler. */
const ROLE_LABEL: Record<UserRole, string> = {
  boss: "İş Sahibi",
  office: "Ofis",
  production: "Üretim",
};

interface UnreadNotification {
  userId: string;
  isRead: boolean;
}

interface TopBarProps {
  onOpenSidebar: () => void;
  /** Komut paletini (Ctrl+K) açar. */
  onOpenPalette?: () => void;
}

export function TopBar({ onOpenSidebar, onOpenPalette }: TopBarProps) {
  const { session, signOut } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const uid = session?.user.uid ?? null;

  /**
   * Okunmamış bildirim sayacı.
   *
   * Sorgudaki `where('userId','==',uid)` ZORUNLUDUR: Security Rules
   * (`notifications` → ⚙️2) yalnızca kendi bildirimini okumaya izin verir,
   * kısıtsız sorgu tamamen reddedilir.
   *
   * orderBy KULLANILMAZ: iki eşitlik filtresi tek alan indeksleriyle
   * karşılanır (bileşik indeks gerekmez). Rozet için sıralamaya gerek yok.
   */
  const unread = useCollection<UnreadNotification>(
    () =>
      uid
        ? query(
            collection(db, "notifications"),
            where("userId", "==", uid),
            where("isRead", "==", false),
            limit(50),
          )
        : null,
    [uid],
  );

  const unreadCount = unread.docs.length;

  // Kullanıcı menüsü: dışarı tıklama + Esc ile kapanır.
  useEffect(() => {
    if (!menuOpen) return;
    const onDocClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  const onSearch = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const data = new FormData(e.currentTarget);
    const q = String(data.get("q") ?? "").trim();
    if (q.length < 2) return;
    navigate(`/search?q=${encodeURIComponent(q)}`);
  };

  return (
    <div className="noprint sticky top-0 z-30 flex h-16 shrink-0 items-center gap-x-4 border-b border-gray-200/80 bg-white/80 px-4 backdrop-blur-xl supports-[backdrop-filter]:bg-white/70 sm:gap-x-6 sm:px-6 lg:px-8">
      <button
        type="button"
        onClick={onOpenSidebar}
        className="-m-2.5 p-2.5 text-gray-700 lg:hidden"
        aria-label="Menüyü aç"
      >
        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
        </svg>
      </button>

      <div className="h-6 w-px bg-gray-900/10 lg:hidden" aria-hidden="true" />

      <div className="flex flex-1 items-center gap-x-4 self-stretch lg:gap-x-6">
        <form className="relative flex max-w-xl flex-1 items-center" onSubmit={onSearch} role="search">
          <label htmlFor="global-search" className="sr-only">
            Sipariş, ürün veya parça ara
          </label>
          <svg
            className="pointer-events-none absolute left-3 h-4 w-4 text-gray-400"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z"
              clipRule="evenodd"
            />
          </svg>
          <input
            id="global-search"
            name="q"
            type="search"
            autoComplete="off"
            placeholder="Ara… (sipariş no, müşteri, ürün)"
            className="block h-11 w-full rounded-lg border-0 bg-gray-100 py-0 pl-9 pr-20 text-sm text-gray-900 ring-1 ring-inset ring-transparent placeholder:text-gray-500 transition focus:bg-white focus:ring-gray-300 lg:h-9"
          />
          {onOpenPalette ? (
            <button
              type="button"
              onClick={onOpenPalette}
              className="absolute right-2 hidden items-center gap-1 rounded-md px-1.5 py-1 text-gray-400 hover:bg-gray-200/70 sm:flex"
              aria-label="Hızlı geçiş (Ctrl+K)"
              title="Hızlı geçiş — sayfalara ve kayıtlara atla"
            >
              <span className="kbd">Ctrl</span>
              <span className="kbd">K</span>
            </button>
          ) : null}
        </form>

        <div className="flex items-center gap-x-3 lg:gap-x-5">
          <Link
            to="/notifications"
            className="relative -m-2.5 rounded-md p-2.5 text-gray-600 hover:text-gray-600"
            aria-label={
              unreadCount > 0 ? `Bildirimler, ${unreadCount} okunmamış` : "Bildirimler"
            }
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" aria-hidden="true">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0"
              />
            </svg>
            {unreadCount > 0 ? (
              <span className="absolute right-0.5 top-0.5 inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-red-600 px-1 py-0.5 text-xs font-bold leading-none text-white">
                {unreadCount >= 50 ? "50+" : unreadCount}
              </span>
            ) : null}
          </Link>

          <div className="hidden lg:block lg:h-6 lg:w-px lg:bg-gray-900/10" aria-hidden="true" />

          <div className="relative" ref={menuRef}>
            <button
              type="button"
              className="flex items-center gap-x-2 rounded-md p-1.5 hover:bg-gray-50"
              id="user-menu-button"
              aria-expanded={menuOpen}
              aria-haspopup="menu"
              onClick={() => setMenuOpen((v) => !v)}
            >
              <span
                className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-100 text-sm font-semibold text-primary-800"
                aria-hidden="true"
              >
                {(session?.displayName ?? "?").trim().charAt(0).toLocaleUpperCase("tr-TR")}
              </span>
              <span className="hidden text-left lg:block">
                <span className="block text-sm font-semibold leading-4 text-gray-900">
                  {session?.displayName}
                </span>
                <span className="block text-xs leading-4 text-gray-500">
                  {session ? ROLE_LABEL[session.role] : ""}
                </span>
              </span>
              <svg className="h-5 w-5 text-gray-600" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path
                  fillRule="evenodd"
                  d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
                  clipRule="evenodd"
                />
              </svg>
            </button>

            {menuOpen ? (
              <div
                className="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-gray-900/5"
                role="menu"
                aria-labelledby="user-menu-button"
              >
                <div className="border-b border-gray-100 px-3 py-2 lg:hidden">
                  <p className="text-sm font-semibold text-gray-900">{session?.displayName}</p>
                  <p className="text-xs text-gray-500">{session ? ROLE_LABEL[session.role] : ""}</p>
                </div>
                <button
                  type="button"
                  role="menuitem"
                  className="block w-full px-3 py-2 text-left text-sm text-gray-900 hover:bg-gray-50"
                  onClick={() => {
                    setMenuOpen(false);
                    void signOut();
                  }}
                >
                  Çıkış
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

export default TopBar;
